import FirebaseAdapter from "@wc/adapters/firebase";

import { INVITE_ERRORS } from "../constants";

/**
 * @typedef {object} InviteValidationResult
 * @property {boolean} valid Whether the invite is valid
 * @property {import('@wc/adapters/firebase/db-models').NoSQLTeamInvite} [invite] The invite if valid
 * @property {string} [error] Error code if invalid
 * @property {string} [teamName] Team name for display
 * @property {string} [orgName] Organization name for display
 */

/**
 * Validates an invite token and returns invite details with team/org info
 * @param {string} token The invite token
 * @returns {Promise<InviteValidationResult>} Whether the invite is valid
 */
export async function validateInvite(token) {
  const invite = await FirebaseAdapter().invites.getInvite(token);

  if (!invite) {
    return { valid: false, error: INVITE_ERRORS.NOT_FOUND };
  }

  if (!invite.isActive) {
    return { valid: false, error: INVITE_ERRORS.INACTIVE };
  }

  const now = new Date();
  const expiresAt = invite.expiresAt?.toDate
    ? invite.expiresAt.toDate()
    : new Date(invite.expiresAt);

  if (expiresAt < now) {
    return { valid: false, error: INVITE_ERRORS.EXPIRED };
  }

  if (invite.maxUses !== null && invite.useCount >= invite.maxUses) {
    return { valid: false, error: INVITE_ERRORS.MAX_USES_REACHED };
  }

  return {
    valid: true,
    invite,
    teamName: invite.teamName,
    orgName: invite.orgName,
    expiresAt,
  };
}
